import React, { useState } from 'react';

import { Button, Card, Form, FormItemProps, Input, message, Typography } from 'antd';

import { InfoCircleOutlined, LinkOutlined, ThunderboltOutlined } from '@ant-design/icons';

type EntityType = 'condominium' | 'company';

const ENTITY_LABELS = {
    condominium: {
        label: 'condomínio',
        article: 'do',
        pronoun: 'seu',
    },
    company: {
        label: 'empresa',
        article: 'da',
        pronoun: 'sua',
    },
};

type Props = {
    name: FormItemProps['name'];
    baseUrl: string;
    currentUrl?: string;
    rules?: FormItemProps['rules'];
    entity?: EntityType;
};



function InfoCard({ baseUrl, entity = 'condominium' }: { baseUrl: string; entity?: EntityType }) {
    const label = ENTITY_LABELS[entity];
    return (
        <Card
            size="small"
            style={{
                backgroundColor: '#e6f7ff',
                borderColor: '#91d5ff',
                marginBottom: 16,
            }}
        >
            <Typography.Paragraph>
                <Typography.Text strong>O que é a URL {label.article} {label.label}?</Typography.Text>
                <br />
                A URL é o endereço que será usado para acessar a página externa {label.article} {label.pronoun} {label.label}.
            </Typography.Paragraph>

            <Typography.Paragraph>
                <Typography.Text strong>Exemplo: </Typography.Text>
                Se você colocar a URL como "jardim-das-flores", o endereço {label.article} {label.pronoun} {label.label} será:{' '}
                <Typography.Link href={`${baseUrl}jardim-das-flores`} target="_blank" style={{ color: '#1890ff' }}>
                    {baseUrl}jardim-das-flores
                </Typography.Link>
                .
            </Typography.Paragraph>

            <Typography.Paragraph>
                <ul style={{ paddingLeft: 20, margin: 0 }}>
                    <li>Use apenas letras minúsculas, números e hífens</li>
                    <li>Não use espaços ou caracteres especiais</li>
                </ul>
            </Typography.Paragraph>

            <Typography.Paragraph>
                <ThunderboltOutlined style={{ color: '#1677ff', marginRight: 4 }} />
                Clique no ícone ao lado do campo para gerar a URL automaticamente.
            </Typography.Paragraph>
        </Card>
    );
}

export function UrlField({ name, baseUrl, currentUrl, rules, entity = 'condominium' }: Props) {
    const [showInfo, setShowInfo] = useState(false);
    const form = Form.useFormInstance();
    const label = ENTITY_LABELS[entity];

    const handleToggleInfo = () => setShowInfo(!showInfo);

    const handleGenerateUrl = () => {
        const nameValue = form.getFieldValue('name') || '';
        if (!nameValue.trim()) {
            message.error('Por favor, preencha o nome antes de gerar a URL.');
            return;
        }
        const generated = nameValue
            .trim()
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '') 
            .replace(/[^a-z0-9\s-]/g, '') 
            .replace(/\s+/g, '-') 
            .replace(/-+/g, '-'); 

        form.setFieldsValue({ [name]: generated });
        message.success('URL gerada automaticamente com sucesso!');
    };

    return (
        <div>
            <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Typography.Text strong>Página {label.article} {label.label} (URL)</Typography.Text>
                {currentUrl && (
                    <Typography.Link href={`${baseUrl}${currentUrl}`} target="_blank">
                        <LinkOutlined />
                    </Typography.Link>
                )}
                <Button
                    type="text"
                    icon={<InfoCircleOutlined />}
                    size="small"
                    onClick={handleToggleInfo}
                />
            </div>

            {showInfo && <InfoCard baseUrl={baseUrl} entity={entity} />}

            <Form.Item name={name} rules={rules}>
                <Input
                    addonBefore={baseUrl.replace('https://', '')}
                    maxLength={500}
                    addonAfter={<ThunderboltOutlined onClick={handleGenerateUrl} 
                        style={{ cursor: 'pointer', color: '#1677ff' }} title="Gerar URL automaticamente" />}
                />
            </Form.Item>
        </div>
    );
}
